// src/pages/CustomersPage.js - Pagina gestione clienti
import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Users,
  Building,
  Mail,
  Phone,
  MapPin,
  Hash,
  RefreshCw,
  CloudOff,
  Clock
} from 'lucide-react';
import ApiService from '../services/api';
import { useOfflineStorage } from '../hooks/useOfflineStorage';
import { useAuth } from '../contexts/AuthContext';
import CustomerModal from '../components/customers/CustomerModal';
import ConfirmDialog from '../components/ui/ConfirmDialog';

const CustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('companyName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const [agentFilter, setAgentFilter] = useState('all'); // all, or specific agent
  const [syncingCustomers, setSyncingCustomers] = useState(false);

  const {
    isOnline,
    getOfflineCustomerOperations,
    syncOfflineCustomers,
    clearSyncedCustomerOperations
  } = useOfflineStorage();

  const { user } = useAuth();
  const isAdmin = user?.isAdmin === true;
  const itemsPerPage = 20;

  // Carica tutti i clienti
  const loadCustomers = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getAllCustomers();
      const safeData = Array.isArray(data) ? data : [];
      setCustomers(safeData);
      setFilteredCustomers(safeData);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
      // Se offline, usa cache
      if (!isOnline) {
        try {
          const cached = JSON.parse(localStorage.getItem('cached_customers') || '[]');
          const safeCached = Array.isArray(cached) ? cached : [];
          setCustomers(safeCached);
          setFilteredCustomers(safeCached);
        } catch (cacheError) {
          console.error('Errore caricamento cache:', cacheError);
          setCustomers([]);
          setFilteredCustomers([]);
        }
      } else {
        // Se online ma errore API, inizializza array vuoti
        setCustomers([]);
        setFilteredCustomers([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  // Filtra e cerca clienti
  useEffect(() => {
    // Assicurati che customers sia un array
    const safeCustomers = Array.isArray(customers) ? customers : [];
    let filtered = [...safeCustomers];

    // Filtro per stato
    if (filter === 'active') {
      filtered = filtered.filter(customer => customer.active !== false);
    } else if (filter === 'inactive') {
      filtered = filtered.filter(customer => customer.active === false);
    }

    // Filtro per agente (solo per admin)
    if (isAdmin && agentFilter !== 'all') {
      filtered = filtered.filter(customer => customer.agent?.name === agentFilter);
    }

    // Ricerca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(customer =>
        customer.companyName?.toLowerCase().includes(term) ||
        customer.vatNumber?.toLowerCase().includes(term) ||
        customer.fiscalCode?.toLowerCase().includes(term) ||
        customer.email?.toLowerCase().includes(term) ||
        customer.city?.toLowerCase().includes(term) ||
        customer.cap?.toLowerCase().includes(term) ||
        customer.legalAddress?.toLowerCase().includes(term)
      );
    }

    // Ordinamento
    filtered.sort((a, b) => {
      const aVal = a[sortBy] || '';
      const bVal = b[sortBy] || '';

      if (sortOrder === 'asc') {
        return aVal.localeCompare(bVal, 'it-IT');
      } else {
        return bVal.localeCompare(aVal, 'it-IT');
      }
    });

    setFilteredCustomers(filtered);
    setCurrentPage(1);
  }, [customers, searchTerm, filter, agentFilter, sortBy, sortOrder, isAdmin]);

  // Paginazione
  const safeFilteredCustomers = Array.isArray(filteredCustomers) ? filteredCustomers : [];
  const totalPages = Math.ceil(safeFilteredCustomers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCustomers = safeFilteredCustomers.slice(startIndex, startIndex + itemsPerPage);

  // Gestione clienti
  const handleCreateCustomer = () => {
    setSelectedCustomer(null);
    setShowModal(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
  };

  const handleDeleteCustomer = (customer) => {
    setCustomerToDelete(customer);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await ApiService.deleteCustomer(customerToDelete.id);
      await loadCustomers();
      setShowDeleteDialog(false);
      setCustomerToDelete(null);
    } catch (error) {
      console.error('Errore eliminazione cliente:', error);
      alert(error.details ? error.details : 'Errore durante l\'eliminazione del cliente');
    }
  };

  const handleSaveCustomer = async (customerData) => {
    try {
      if (selectedCustomer) {
        // Modifica - usa lo stesso endpoint senza ID
        await ApiService.updateCustomer(customerData);
      } else {
        // Creazione
        await ApiService.createCustomer(customerData);
      }

      await loadCustomers();
      setShowModal(false);
      setSelectedCustomer(null);
    } catch (error) {
      console.error('Errore salvataggio cliente:', error);
      throw error;
    }
  };

  // Sincronizza clienti offline
  const handleSyncOfflineCustomers = async () => {
    if (!isOnline) {
      alert('Connessione non disponibile. Riprova quando torni online.');
      return;
    }

    setSyncingCustomers(true);
    try {
      const result = await syncOfflineCustomers();

      if (result.success) {
        alert(`Sincronizzazione completata: ${result.syncedCount} operazioni sincronizzate`);
        await loadCustomers(); // Ricarica la lista aggiornata
      } else {
        alert(`Sincronizzazione parziale: ${result.syncedCount}/${result.totalCount} operazioni completate. ${result.errors.length} errori.`);
      }
    } catch (error) {
      console.error('Errore sincronizzazione clienti:', error);
      alert('Errore durante la sincronizzazione. Riprova più tardi.');
    } finally {
      clearSyncedCustomerOperations();
      setSyncingCustomers(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  // Ottieni operazioni offline pendenti
  const offlineOperations = getOfflineCustomerOperations();
  const pendingCustomerOperations = offlineOperations.length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestione Clienti</h1>
              <p className="text-sm text-gray-600">
                {safeFilteredCustomers.length} di {Array.isArray(customers) ? customers.length : 0} clienti
                {!isOnline && ' (modalità offline)'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {/* Pulsante sincronizzazione offline */}
            {pendingCustomerOperations > 0 && (
              <button
                onClick={handleSyncOfflineCustomers}
                disabled={!isOnline || syncingCustomers}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${isOnline && !syncingCustomers
                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  : 'bg-gray-100 text-gray-500 cursor-not-allowed'
                  }`}
                title={isOnline ? 'Sincronizza operazioni offline' : 'Connessione non disponibile'}
              >
                <RefreshCw className={`w-4 h-4 ${syncingCustomers ? 'animate-spin' : ''}`} />
                <span>Sincronizza ({pendingCustomerOperations})</span>
              </button>
            )}

            <button
              onClick={loadCustomers}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Aggiorna</span>
            </button>

            <button
              onClick={handleCreateCustomer}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Nuovo Cliente</span>
            </button>
          </div>
        </div>

        {/* Banner operazioni offline */}
        {pendingCustomerOperations > 0 && (
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CloudOff className="w-5 h-5 text-orange-600" />
                <div>
                  <h3 className="text-sm font-medium text-orange-900">
                    Operazioni Offline Pendenti
                  </h3>
                  <p className="text-sm text-orange-700">
                    {pendingCustomerOperations} operazione{pendingCustomerOperations !== 1 ? 'i' : ''} in attesa di sincronizzazione
                  </p>
                </div>
              </div>
              <button
                onClick={handleSyncOfflineCustomers}
                disabled={!isOnline || syncingCustomers}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isOnline && !syncingCustomers
                  ? 'bg-orange-600 text-white hover:bg-orange-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
              >
                {syncingCustomers ? 'Sincronizzando...' : 'Sincronizza Ora'}
              </button>
            </div>
          </div>
        )}

        {/* Filtri e ricerca */}
        <div className={`grid grid-cols-1 gap-4 ${isAdmin ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
          {/* Ricerca */}
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Cerca per nome, P.IVA, email, città, CAP..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filtro stato */}
          <div>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tutti i clienti</option>
              <option value="active">Clienti attivi</option>
              <option value="inactive">Clienti inattivi</option>
            </select>
          </div>

          {/* Filtro agente (solo per admin) */}
          {isAdmin && (
            <div>
              <select
                value={agentFilter}
                onChange={(e) => setAgentFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Tutti gli agenti</option>
                {Array.from(new Set(customers.map(c => c.agent?.name).filter(Boolean))).sort().map(agentName => (
                  <option key={agentName} value={agentName}>{agentName}</option>
                ))}
              </select>
            </div>
          )}

          {/* Ordinamento */}
          <div>
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="companyName-asc">Nome A→Z</option>
              <option value="companyName-desc">Nome Z→A</option>
              <option value="city-asc">Città A→Z</option>
              <option value="city-desc">Città Z→A</option>
              <option value="createdAt-desc">Più recenti</option>
              <option value="createdAt-asc">Più vecchi</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista clienti */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('companyName')}
                    >
                      <div className="flex items-center space-x-1">
                        <Building className="w-4 h-4" />
                        <span>Azienda {getSortIcon('companyName')}</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <Hash className="w-4 h-4" />
                        <span>P.IVA</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <Hash className="w-4 h-4" />
                        <span>Codice Fiscale</span>
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('city')}
                    >
                      <div className="flex items-center space-x-1">
                        <MapPin className="w-4 h-4" />
                        <span>Città {getSortIcon('city')}</span>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div className="flex items-center space-x-1">
                        <Mail className="w-4 h-4" />
                        <span>Contatti</span>
                      </div>
                    </th>
                    {isAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-1">
                          <Users className="w-4 h-4" />
                          <span>Agente riferimento</span>
                        </div>
                      </th>
                    )}
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedCustomers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {customer.companyName || 'N/A'}
                            </span>
                            {customer.isOffline && (
                              <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full flex items-center space-x-1">
                                <CloudOff className="w-3 h-3" />
                                <span>Offline</span>
                              </span>
                            )}
                            {customer.isOfflineModified && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full flex items-center space-x-1">
                                <Clock className="w-3 h-3" />
                                <span>Da Sync</span>
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {customer.legalAddress}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">
                          {customer.vatNumber || 'N/A'}
                        </div>
                        {customer.sdiCode && (
                          <div className="text-xs text-gray-500">
                            SDI: {customer.sdiCode}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 font-mono">
                          {customer.fiscalCode || 'N/A'}
                        </div>
                      </td>                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {customer.city || 'N/A'}
                        </div>
                        {customer.cap && (
                          <div className="text-xs text-gray-500">
                            CAP: {customer.cap}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {customer.email && (
                            <div className="flex items-center space-x-1">
                              <Mail className="w-3 h-3 text-gray-400" />
                              <span>{customer.email}</span>
                            </div>
                          )}
                          {customer.phone && (
                            <div className="flex items-center space-x-1 mt-1">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <span>{customer.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      {isAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="flex items-center space-x-1">
                              <Users className="w-3 h-3 text-gray-400" />
                              <span>{customer.agent?.name || 'N/A'}</span>
                            </div>
                            {customer.createdAt && (
                              <div className="flex items-center space-x-1 mt-1">
                                <Clock className="w-3 h-3 text-gray-400" />
                                <span className="text-xs">
                                  {new Date(customer.createdAt).toLocaleDateString('it-IT')}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEditCustomer(customer)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="Modifica cliente"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCustomer(customer)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Elimina cliente"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {safeFilteredCustomers.length === 0 && !loading && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Nessun cliente trovato
                </h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm ? 'Prova a modificare i criteri di ricerca' : 'Inizia creando il tuo primo cliente'}
                </p>
                {!searchTerm && (
                  <button
                    onClick={handleCreateCustomer}
                    className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Crea primo cliente</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Paginazione */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700">
                Mostra {startIndex + 1}-{Math.min(startIndex + itemsPerPage, safeFilteredCustomers.length)} di {safeFilteredCustomers.length} clienti
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Precedente
                </button>

                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 border rounded-lg ${currentPage === pageNum
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Successiva
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal cliente */}
      {showModal && (
        <CustomerModal
          customer={selectedCustomer}
          onSave={handleSaveCustomer}
          onClose={() => {
            setShowModal(false);
            setSelectedCustomer(null);
          }}
          user={user}
        />
      )}

      {/* Dialog conferma eliminazione */}
      {showDeleteDialog && (
        <ConfirmDialog
          title="Elimina Cliente"
          message={`Sei sicuro di voler eliminare il cliente "${customerToDelete?.companyName}"? Questa azione non può essere annullata.`}
          confirmText="Elimina"
          confirmStyle="danger"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteDialog(false);
            setCustomerToDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default CustomersPage;